import classnames from 'classnames'
import { GlobalGridPreset } from '~/@types/grid-preset'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'

import Ratio from '~/components/Abstracts/Ratio'
import AddToCart from '~/components/AddToCart'
import { CartPopInHeader } from '~/components/Cart/CartPopInHeader'
import { CustomizationPreview } from '~/components/UI/CustomizationPreview'
import Image from '~/components/UI/Image'

import { useProductHeaderProvider } from '~/providers/ProductPageProvider'
import { useStyle } from '~/providers/StyleProvider'
import { useUserSelectionProvider } from '~/providers/UserSelectionProvider'

import useBodyScrollLock from '~/hooks/useBodyScrollLock'
import { useGetStateInformationsFromUserSelection } from '~/hooks/useGetStateInformationsFromUserSelection'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export interface MultiBundlePreviewProps {
  className?: string
  close?(): void
}

const IMAGE_SIZES = [{ breakpoint: 'md', ratio: 1 / 3 }, { ratio: 1 }]

export function MultiBundlePreview({
  className,
  close,
}: MultiBundlePreviewProps) {
  useBodyScrollLock(true)

  const gridStyle = useStyle({
    grid: GlobalGridPreset.BASE_GRID,
  })
  const { data } = useGetStateInformationsFromUserSelection()
  const { previewSources, addToCartDefaultProps } = useProductHeaderProvider()
  const { userSelection } = useUserSelectionProvider()

  const titleStyle = useStyle({
    textPreset: GlobalTextPreset.Title12Primary,
    textStyling: GlobalTextStyling.UpperCase,
  })
  const addToCartProps = {
    ...addToCartDefaultProps,
    onSuccess: () => {
      addToCartDefaultProps?.onSuccess?.()
      close()
    },
  }

  return (
    <div className={cx(css.MultiBundlePreview, className)}>
      <CartPopInHeader className={cx(css.header)} close={close} noBorder />
      <div className={cx(css.container, gridStyle)}>
        <div
          className={cx(
            css.content,
            css?.[`layout-${previewSources?.length}`],
          )}>
          {previewSources?.map((preview, index) => {
            const tab = userSelection[index]
            const activeProduct = data[index]?.find(
              (product) => product.shopifyId === tab.productId,
            )

            const activeVariant =
              activeProduct?.variants.find(
                (variant) => variant.id === tab.variantId,
              ) ?? null

            const isCustomizable = activeProduct?.byCharlotCustomizable

            const customizerVariants = isCustomizable
              ? activeProduct.customizationProducts
                  ?.map((product) => ({
                    type: product?.productType,
                    variant: product.variants.find(
                      (variant) => variant.id === tab[product.productType],
                    ),
                  }))
                  .filter((entry) => entry.variant)
              : []

            const simpleImageProps = {
              asPlaceholder: true,
              src: isCustomizable
                ? customizerVariants?.[0]?.variant?.variantImage?.src
                : activeVariant?.image?.src,
            }

            return (
              <div key={`preview_product_${index}`} className={cx(css.product)}>
                {Object.entries(preview ?? {})?.length > 0 ? (
                  <Ratio className={cx(css.image)} ratio={0.75}>
                    {() => (
                      <CustomizationPreview
                        className={cx(css.preview)}
                        sources={preview}
                      />
                    )}
                  </Ratio>
                ) : (
                  <Image
                    className={cx(css.image)}
                    ratio={0.75}
                    objectFit="cover"
                    layout="fill"
                    {...simpleImageProps}
                  />
                )}
                {activeProduct?.productName && (
                  <div className={cx(css.title, titleStyle)}>
                    {activeProduct?.productName}
                  </div>
                )}
              </div>
            )
          })}
        </div>
        <div className={cx(css.ctaContainer, css.hideOnSmallScreen)}>
          <AddToCart className={cx(css.addToCart)} {...addToCartProps} />
        </div>
      </div>
      <div className={cx(css.ctaContainer, css.hideOnLargeScreen)}>
        <AddToCart className={cx(css.addToCart)} {...addToCartProps} />
      </div>
    </div>
  )
}
